import Vue from "vue";

/**
 * Роли всегда как массив slug. В localStorage раньше писали массив как строку "a,b" — после F5 ломались проверки allowedRoles.
 */
function normalizeRoles(role) {
    if (role == null || role === '') {
        return [];
    }
    if (Array.isArray(role)) {
        return role.map((r) => String(r).trim()).filter(Boolean);
    }
    if (typeof role === 'string') {
        const t = role.trim();
        if (t.startsWith('[')) {
            try {
                const p = JSON.parse(t);
                return Array.isArray(p) ? normalizeRoles(p) : [];
            } catch (e) {
                /* legacy */
            }
        }
        return t.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [String(role)];
}

/** При обновлении страницы роутер читает store до App.vue — берём роли из сохранённого user (как после логина), иначе из ключа userRoles */
function loadUserRolesFromStorage() {
    try {
        const userRaw = localStorage.getItem('user');
        if (userRaw && userRaw !== 'undefined') {
            const user = JSON.parse(userRaw);
            if (user && user.role) {
                const fromUser = normalizeRoles(user.role);
                if (fromUser.length) {
                    return fromUser;
                }
            }
        }
    } catch (e) {
        /* ignore */
    }
    const raw = localStorage.getItem('userRoles');
    if (!raw || raw === 'undefined') {
        return [];
    }
    return normalizeRoles(raw);
}

export const AUTH_REQUEST = "AUTH_REQUEST";
export const AUTH_SUCCESS = "AUTH_SUCCESS";
export const AUTH_ERROR = "AUTH_ERROR";
export const AUTH_LOGOUT = "AUTH_LOGOUT";

export const USER_REQUEST = "USER_REQUEST";
export const USER_SUCCESS = "USER_SUCCESS";
export const USER_ERROR = "USER_ERROR";

const auth = {
    namespaced: true,
    state: {
        token: localStorage.getItem('user-token') || '',
        status: '',
        user: localStorage.getItem('user') || {},
        userRoles: loadUserRolesFromStorage(),
        tempLink: null,
        agentCompany: JSON.parse(localStorage.getItem('agentCompany')) || null, 
    },

    mutations: {
        [AUTH_REQUEST]: (state) => {
            state.status = 'loading'
        },

        [AUTH_SUCCESS]: (state, token) => {
            state.status = 'success'
            state.token = token
        },

        [AUTH_ERROR]: (state) => {
            state.status = 'error'
        },
        [USER_REQUEST]: state => {
            state.status = "loading";
        },
        [USER_SUCCESS]: (state, resp) => {
            state.status = "success";
            Vue.set(state, "user", resp);
            if (resp && resp.role) {
                const roles = normalizeRoles(resp.role);
                state.userRoles = roles;
                localStorage.setItem('userRoles', JSON.stringify(roles));
            }
        },
        [USER_ERROR]: state => {
            state.status = "error";
        },
        [AUTH_LOGOUT]: state => {
            state.user = {};
            state.token = '';
            state.status = "error";
            state.userRoles = [];
            state.tempLink = null;
            state.agentCompany = null;
        },
        SET_ROLE(state, role) {
            state.userRoles = normalizeRoles(role);
        },
        SET_TEMP_LINK(state, tempLink) {
            state.tempLink = tempLink;
        },
        SET_AGENT_COMPANY(state, company) {
            state.agentCompany = company;
        },        
    },

    actions: {
        [AUTH_REQUEST]: ({ commit, dispatch }, user) => {
            return new Promise((resolve, reject) => {
                commit(AUTH_REQUEST);
                axios({ url: '/api/login', data: user, method: 'POST' })
                    .then(resp => {
                        const token = resp.data.token;
                        const user = JSON.stringify(resp.data.user);
                        localStorage.setItem('user-token', token);
                        localStorage.setItem('user', user);
                        const roles = normalizeRoles(resp.data.user.role);
                        localStorage.setItem('userRoles', JSON.stringify(roles));

                        dispatch(USER_REQUEST, user);
                        commit('SET_ROLE', roles);
                        axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                        commit(AUTH_SUCCESS, token);
        
                        // ✅ Если роль agent, получаем агентскую компанию
                        if (resp.data.user.role.includes('agent')) {
                            axios.get('/api/agent-companies/my').then(agentResp => {
                                const agentCompany = agentResp.data;
                                commit('SET_AGENT_COMPANY', agentCompany);
                                localStorage.setItem('agentCompany', JSON.stringify(agentCompany));
                            }).catch(() => {
                                commit('SET_AGENT_COMPANY', null);
                                localStorage.setItem('agentCompany', null);

                                console.warn('Не удалось загрузить агентскую компанию');
                            });
                        }
        
                        // Инициализируем и синхронизируем настройки таблиц с БД после входа
                        dispatch('userLocalSettings/INITIALIZE_AND_SYNC', null, { root: true });
        
                        resolve(resp);
                    })
                    .catch(err => {
                        commit(AUTH_ERROR, err);
                        localStorage.removeItem('user-token');
                        reject(err);
                    });
            });
        },
        [AUTH_LOGOUT]: ({commit, dispatch}) => {
            return new Promise((resolve, reject) => {
                commit(AUTH_LOGOUT)
                localStorage.clear();
                // localStorage.removeItem('user-token');
                // localStorage.removeItem('user');
                // localStorage.removeItem('userRoles');
                // localStorage.removeItem('agentCompany');

                // Сбрасываем состояние настроек пользователя в Vuex store
                dispatch('userLocalSettings/RESET_USER_SETTINGS', null, { root: true });

                // remove the axios default header
                delete axios.defaults.headers.common['Authorization'];
                resolve();
            })
        },
        [USER_REQUEST]: ({ commit, dispatch }) => {
            if (!!localStorage.getItem('user')) {
                commit(USER_SUCCESS, JSON.parse(localStorage.getItem('user')))
            } else {
                commit(USER_ERROR);
                // if resp is unauthorized, logout, to
                dispatch(AUTH_LOGOUT);
            }
        },
        checkUserStatus({ commit, dispatch, state }) {
            if (!state.token) return;
            
            return new Promise((resolve, reject) => {
                axios.get('/api/user')
                    .then(response => {
                        if (response.data.fired) {
                            dispatch(AUTH_LOGOUT);
                            if (window.location.pathname !== '/login') {
                                window.location.href = '/login';
                            }
                        }
                        resolve(response);
                    })
                    .catch(error => {
                        if (error.response && error.response.status === 401) {
                            dispatch(AUTH_LOGOUT);
                            if (window.location.pathname !== '/login') {
                                window.location.href = '/login';
                            }
                        }
                        reject(error);
                    });
            });
        },
        refreshAgentCompany({ commit, state }) {
            return new Promise((resolve, reject) => {
                if (state.userRoles.includes('agent')) {
                    axios.get('/api/agent-companies/my')
                        .then(response => {
                            const agentCompany = response.data;
                            commit('SET_AGENT_COMPANY', agentCompany);
                            localStorage.setItem('agentCompany', JSON.stringify(agentCompany));
                            resolve(agentCompany);
                        })
                        .catch(error => {
                            console.warn('Failed to refresh agent company:', error);
                            commit('SET_AGENT_COMPANY', null);
                            localStorage.setItem('agentCompany', null);

                            reject(error);
                        });
                } else {
                    reject('User is not an agent');
                }
            });
        }        
    },

    getters: {
        isAuthenticated: state => !!state.token,
        authStatus: state => state.status,
        currentUser: state => state.user,
        isUserLoaded: state => !!state.user.name,
        getAgentCompany: state => state.agentCompany,
        headDepartment(state, getters) {
            if (state.user.role && getters.statusUser === 'head') {
                return state.user.role[0].replace('-head', '')
            }
        },
        roleNoHead(state, getters) {
            if (state.user.role && getters.statusUser === 'head') {
                return state.user.role[0].replace('-head', '')
            } 

            return state.user.role[0]
        },
        statusUser(state) {
            if (
                state.user.role.includes("administrator") ||
                state.user.role.includes("head-accountant") ||
                state.user.role.includes("president") ||
                state.user.role.includes("accountant") 
                // ||
                // state.user.role.includes("sales-head-manager")
            ) {
                return 'celebrity'
            }

            if (
                state.user.role.includes("logistics-head-manager") ||
                state.user.role.includes("logistics-multim-head-manager") ||
                state.user.role.includes("TO-head-manager") || 
                state.user.role.includes("sales-head-manager") || 
                state.user.role.includes("b2c-head-manager")
            ) {
                return 'head'
            }
            
            if (
                state.user.role.includes("sales-manager") ||
                state.user.role.includes("logistics-manager") ||
                state.user.role.includes("logistics-multim-manager") ||
                state.user.role.includes("bsz-manager") ||
                state.user.role.includes("TO-manager") ||
                state.user.role.includes("b2c-manager")
              ) {
                return "manager";
              }
        },
        getUserRoles: state => state.userRoles,
        getTempLink: state => state.tempLink,
    }
}

export default {
    ...auth
}
